import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useDemoMode } from '../contexts/DemoModeContext';

// Padrão é sempre claro (pedido explícito do proprietário) — só entra em
// modo escuro se a pessoa já tiver escolhido isso antes neste navegador.
// Nunca segue a preferência do sistema operacional.
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('ca-render-afiliados-theme') === 'dark';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    window.localStorage.setItem('ca-render-afiliados-theme', dark ? 'dark' : 'light');
  }, [dark]);

  return [dark, setDark] as const;
}

const NAV_LINK_CLASS = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-1.5 font-display text-sm font-medium border-b-2 transition-colors ${
    isActive
      ? 'border-brand-500 text-[rgb(var(--text))]'
      : 'border-transparent text-[rgb(var(--text-muted))] hover:text-[rgb(var(--text))]'
  }`;

interface LayoutProps {
  children: ReactNode;
  nav?: Array<{ to: string; label: string }>;
  onSignOut: () => void;
}

export function Layout({ children, nav, onSignOut }: LayoutProps) {
  const [dark, setDark] = useDarkMode();
  const demoMode = useDemoMode();

  // No modo de demonstração, "Sair" só precisa desligar o modo — nunca
  // chama o signOut/logout de verdade (não existe sessão real pra
  // encerrar). Fora do modo de demonstração, comportamento inalterado.
  const handleSignOut = demoMode.enabled ? demoMode.disable : onSignOut;

  return (
    <div className="min-h-screen">
      {demoMode.enabled ? (
        <div className="bg-warning px-4 py-1.5 text-center text-xs font-semibold uppercase tracking-wider text-black">
          Modo de demonstração — dados fictícios, nada aqui é real
        </div>
      ) : null}
      <header className="border-b border-[rgb(var(--border))]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-6">
            <span className="font-display text-base font-bold tracking-tight">
              CA Render<span className="text-brand-500">.</span> <span className="font-normal text-[rgb(var(--text-muted))]">Painel de Afiliados</span>
            </span>
            {nav && nav.length > 0 ? (
              <nav className="flex items-center gap-1">
                {nav.map((item) => (
                  <NavLink key={item.to} to={item.to} className={NAV_LINK_CLASS} end>
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDark(!dark)}
              className="btn-secondary !px-2.5"
              aria-label={dark ? 'Ativar tema claro' : 'Ativar tema escuro'}
              title={dark ? 'Tema claro' : 'Tema escuro'}
            >
              {dark ? '☀️' : '🌙'}
            </button>
            <button type="button" className="btn-secondary" onClick={handleSignOut}>
              Sair
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">{children}</main>
    </div>
  );
}
